import { Modal } from 'antd';
import React, { FC, useState } from 'react';
import { useQuery } from 'react-query';
import styled from 'styled-components';
import { CredusButton, CredusText, CredusWrapper } from 'ui';
import testApi from '../../../../api/test';
import { BiChevronRight } from 'react-icons/bi';
import { BreadCrumb } from '../../../../pages/TestManagement';
import EmptyState from '../../../../shared/EmptyState';
import { GalleryImage } from '../GalleryCard';

interface ProctoringModal {
  candidateId: number;
  testId: number;
  open: boolean;
  onSetModal: React.Dispatch<React.SetStateAction<boolean>>;
}

const ProctoringModal: FC<ProctoringModal> = ({
  onSetModal,
  open,
  candidateId,
  testId,
}) => {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(9);
  const [images, setImages] = useState([{ base64String: '', dateCreated: '' }]);
  const [previewImage, setPreviewImage] = useState<string | null>(null); // State to store the image being previewed
  useQuery({
    queryKey: ['candidateProctoredData', candidateId, testId, page, pageSize],
    queryFn: async () =>
      testApi.getProctoredImages(candidateId, testId, page, pageSize),
    onSuccess(data) {
      if (data.data.images) setImages(data.data.images);
    },
    enabled: !!candidateId,
  });

  const handleImageClick = ({data}: {data: string}) => {
    setPreviewImage(data); // Set the clicked image for preview
  };

  const handleClosePreview = () => {
    setPreviewImage(null); // Close the preview by resetting the state
  };

  const handlePreviousPage = () => {
    if (page > 1) {
      setPage(page - 1); // Go to the previous page
    }
  };

  const handleNextPage = () => {
    setPage(page + 1); // Go to the next page
  };

  const GalaryData = images
    .sort(
      (a, b) =>
        new Date(b.dateCreated).getTime() - new Date(a.dateCreated).getTime()
    )
    .map((image) => image);

  return (
    <>
      <Modal
        centered
        title={
          <BreadCrumb>
            <CredusText color={'#7a7a7a'} size="sm">
              Live Proctoring Captures
            </CredusText>
            <BiChevronRight color={'#7a7a7a'} />
            <CredusText color={'#7a7a7a'} size="sm">
              Captures
            </CredusText>
          </BreadCrumb>
        }
        open={open}
        footer={false}
        onOk={() => onSetModal(false)}
        onCancel={() => onSetModal(false)}
        width={720}
      >
        <Container>
          <SelectInput>
            <input type="checkbox" /> <span>Select All</span>
          </SelectInput>

          {GalaryData.length > 0 ? (
            <GalleryWrap>
              {GalaryData.map((image, id) => (
                <GalleryImage
                  key={id}
                  base64String={image.base64String}
                  dateCreated={image.dateCreated}
                  onClick={() => handleImageClick({data: image.base64String})}
                />
              ))}
            </GalleryWrap>
          ) : (
            <NoImage>
              <EmptyState description="No available data " margin="4rem 0rem" />
            </NoImage>
          )}

          {/* Preview Modal for clicked image */}
          {previewImage && (
            <PreviewModal>
              <div className="preview-modal">
                <div className="preview-close" onClick={handleClosePreview}>
                  X
                </div>
                <img
                  src={`data:image/png;base64,${previewImage}`}
                  alt="preview-image"
                  style={{
                    width: '480px',
                    maxHeight: '80vh',
                    objectFit: 'contain',
                  }}
                />
              </div>
            </PreviewModal>
          )}

          <CredusWrapper mt=".8rem" display="flex" justify="center">
            <CredusWrapper mb="4rem" display="flex" gap="1.4rem">
              <CredusButton
                onClick={handlePreviousPage}
                variant="secondary"
                disabled={page === 1} // Disable Previous button when on the first page
              >
                Previous Page
              </CredusButton>
              <CredusButton onClick={handleNextPage} variant="primary">
                Next Page
              </CredusButton>
            </CredusWrapper>
          </CredusWrapper>
        </Container>
      </Modal>
    </>
  );
};

export default ProctoringModal;

const Container = styled.div`
  padding: 0rem 0.8rem;
`;

const SelectInput = styled.div`
  font-size: 10px;
  margin: 29px 0;
  display: flex;
  gap: 5px;
  align-items: center;
  justify-content: end;
`;

const PreviewModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  .preview-modal {
    background: white;
    padding: 1rem;
    width: 600px;
    height: 400px;
    max-width: 90%;
    max-height: 90%;
    overflow: auto;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .preview-close {
    position: absolute;
    top: 10px;
    right: 10px;
    font-size: 16px;
    color: #000;
    padding: 5px;
    cursor: pointer;
  }
`;

const GalleryWrap = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 10px;
  grid-auto-rows: minmax(100px, auto);
`;

const NoImage = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;
